import React, { useEffect, useMemo, useState } from "react";
import { Box, Flex } from "theme-ui";
import moment from "moment-timezone";

import { themevals } from "theme/themevals";
import BasicButton from "components/forms/BasicButton";
import { useToast } from "components/toast";
import { getProposal, getProposalSenders, type ProposalSender } from "api/Proposals";
import {
  addMoContact,
  generateMoProposals,
  getMo,
  notThemMo,
  sendMoSite,
  type MoContact,
  type MoGenerateResult,
  type MoRow,
} from "api/MaintenanceOpportunities";

/**
 * REVIEW & SEND (2026-10-19, design: docs/maintenance-module/OPPORTUNITY_TO_FOLLOWUP_DESIGN.md).
 * One site at a time: check the owner, tick the people, read and edit the
 * email (Mission Control's draft, else the site's story template), Send.
 * Each ticked person gets their OWN email, proposal link and lead; the
 * server does it all in one call. The site's proposal is prepared when the
 * site opens here, so Preview works before anything is sent.
 */

type Outcome = "sent" | "skipped" | "not_them" | "unavailable";
const SKIP_TEXT: Record<string, string> = {
  no_end_customer: "Nobody is named as the owner yet",
  no_coordinates: "The site has no map position, so it cannot be priced",
  no_covering_depot: "No depot covers this site",
  not_open: "This opportunity is closed",
  draft_in_wizard: "A draft exists for this site - finish it in the proposal wizard",
  error: "Could not prepare a proposal",
};
const BAND_LABEL: Record<string, string> = {
  coming_due: "Coming due",
  due: "Due now",
  overdue: "Overdue - depth check offered",
  long: "Installed 3+ years ago - depth check offered",
};

const sub: React.CSSProperties = { fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey };
const label: React.CSSProperties = { display: "block", fontFamily: "Poppins", fontSize: 11, fontWeight: 600, color: "#555", margin: "14px 0 4px", textTransform: "uppercase", letterSpacing: 0.3 };
const input: React.CSSProperties = { width: "100%", boxSizing: "border-box", border: "1px solid #E4E7EB", borderRadius: 6, padding: "7px 10px", fontFamily: "Poppins", fontSize: 13 };
// Explicit, so the app's global label/checkbox styles (uppercase, full-width inputs) do not leak in.
const check = { appearance: "auto", WebkitAppearance: "checkbox", width: 16, height: 16, minWidth: 16, margin: "2px 0 0", padding: 0, flex: "none", cursor: "pointer" } as React.CSSProperties;
const pickRow: React.CSSProperties = { display: "flex", gap: 10, alignItems: "flex-start", padding: "8px 0", borderBottom: "1px solid #F1F2F4", fontFamily: "Poppins", fontSize: 12, textTransform: "none", letterSpacing: 0, cursor: "pointer", fontWeight: 400 };
const tab = (on: boolean): React.CSSProperties => ({ fontFamily: "Poppins", fontSize: 12, padding: "5px 10px", borderRadius: 6, border: on ? "1px solid #111" : "1px solid #E4E7EB", background: on ? "#111" : "#fff", color: on ? "#fff" : "#444", cursor: "pointer" });

/** "Email shown on https://site.org/wp-content/…pdf" -> "site.org" */
function where(c: MoContact): string | null {
  const m = String(c.reason ?? "").match(/https?:\/\/([^/\s?]+)/i);
  if (m) return m[1].replace(/^www\./, "");
  return c.reason ? String(c.reason).slice(0, 60) : null;
}

/** Same rule as the server: the first name, or "there". */
function fill(text: string, name: string | null | undefined): string {
  const first = String(name ?? "").trim().split(/\s+/)[0] || "";
  const ok = /^[A-Za-z][A-Za-z'-]+$/.test(first);
  return text.replace(/\{firstName\}/g, ok ? first : "there");
}

const ReviewSend: React.FC<{ ids: string[]; onClose: () => void }> = ({ ids, onClose }) => {
  const toast = useToast();
  const [index, setIndex] = useState(0);
  const [row, setRow] = useState<MoRow | null>(null);
  const [gen, setGen] = useState<MoGenerateResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [outcomes, setOutcomes] = useState<Record<string, Outcome>>({});
  const [owner, setOwner] = useState("");
  const [picked, setPicked] = useState<string[]>([]);
  const [previewKey, setPreviewKey] = useState<string | null>(null);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [draftBy, setDraftBy] = useState<string>("template");
  const [includePassword, setIncludePassword] = useState(true);
  const [newEmail, setNewEmail] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  // "From" (2026-10-19, Jack): who the proposal and email come from, as in the wizard and Resend.
  const [senders, setSenders] = useState<ProposalSender[]>([]);
  const [senderId, setSenderId] = useState("");
  useEffect(() => { getProposalSenders().then((s) => setSenders(Array.isArray(s) ? s : [])); }, []);

  const currentId = ids[index];
  const done = index >= ids.length;

  const fillFrom = (r: MoRow) => {
    setRow(r);
    setOwner(r.endCustomer?.name || r.client?.name || "");
    const withEmail = (r.trail ?? []).filter((c) => c.kind !== "organization" && !c.rejectedAt && c.email);
    const ticked = withEmail.filter((c) => c.selected).map((c) => c.key);
    setPicked(ticked.length ? ticked : withEmail.slice(0, 1).map((c) => c.key));
    setPreviewKey(null);
    const draft = r.emailDraft && !r.emailDraft.sentAt ? r.emailDraft : null;
    const d = draft ?? r.story?.defaultEmail ?? { subject: "", message: "" };
    setSubject(d.subject);
    setMessage(d.message);
    setDraftBy(draft ? draft.by : "template");
    setIncludePassword(true);
    setNewEmail("");
  };

  // Load a site; make (or reuse) its proposal so Preview works.
  useEffect(() => {
    if (done) return;
    let live = true;
    (async () => {
      setLoading(true);
      setRow(null);
      setGen(null);
      const r: any = await getMo(currentId);
      if (!live) return;
      if (r?.error) { toast.add(r.error, "var(--theme-ui-colors-danger)"); setLoading(false); return; }
      let g: MoGenerateResult | null = null;
      if (!r.sends?.sent) {
        const res: any = await generateMoProposals([currentId]);
        g = res?.results?.[0] ?? { opportunityId: currentId, proposalId: null, shareUrl: null, password: null, expiresAt: null, status: null, created: false, reason: "error", message: res?.error };
      }
      if (!live) return;
      setGen(g);
      fillFrom(r);
      // Default From = who the site's proposal is from now (owner, default owner, or you).
      const pid = g?.proposalId ?? r.proposal?.id;
      if (pid) {
        const p: any = await getProposal(pid);
        if (live) setSenderId(p?.sender?.id ?? "");
      } else setSenderId("");
      setLoading(false);
    })();
    return () => { live = false; };
  }, [index]); // eslint-disable-line

  const people = useMemo(() => (row?.trail ?? []).filter((c) => c.kind !== "organization" && !c.rejectedAt && c.email), [row]);
  const chosen = people.filter((c) => picked.includes(c.key));
  const preview = chosen.find((c) => c.key === previewKey) ?? chosen[0] ?? null;
  const alreadySent = !!row?.sends?.sent;
  const unavailable = !alreadySent && !!gen && !gen.shareUrl;

  const next = (o: Outcome) => {
    setOutcomes((s) => ({ ...s, [currentId]: o }));
    setIndex((i) => i + 1);
  };

  const toggle = (key: string) => setPicked((p) => (p.includes(key) ? p.filter((k) => k !== key) : [...p, key]));
  const addEmail = async () => {
    if (!row || !newEmail.trim()) return;
    const r: any = await addMoContact(row.id, { email: newEmail.trim() });
    if (r?.error) return toast.add(r.error, "var(--theme-ui-colors-danger)");
    const added = (r.trail ?? []).find((c: MoContact) => (c.email ?? "").toLowerCase() === newEmail.trim().toLowerCase());
    setRow(r);
    if (added) setPicked((p) => (p.includes(added.key) ? p : [...p, added.key]));
    setNewEmail("");
  };

  const send = async () => {
    if (!row || !chosen.length) return;
    setBusy("send");
    const res: any = await sendMoSite(row.id, { recipients: chosen.map((c) => c.key), subject, message, includePassword, ownerName: owner.trim() || null, senderUserId: senderId || undefined });
    setBusy(null);
    if (res?.error) return toast.add(res.error, "var(--theme-ui-colors-danger)");
    const failed = (res.results ?? []).filter((r: any) => !r.ok);
    if (failed.length) {
      toast.add(`Sent ${res.sent} of ${res.results.length}. Failed: ${failed.map((f: any) => `${f.email} (${f.error})`).join("; ")}`, "var(--theme-ui-colors-warning)");
    } else {
      toast.add(`Sent ${res.sent} email${res.sent === 1 ? "" : "s"}, each with its own link.`, "var(--theme-ui-colors-success)");
    }
    if (res.sent) next("sent");
  };
  const notThem = async () => {
    if (!row) return;
    setBusy("nt");
    const r: any = await notThemMo(row.id);
    setBusy(null);
    if (r?.error) return toast.add(r.error, "var(--theme-ui-colors-danger)");
    next("not_them");
  };

  const counts = Object.values(outcomes).reduce((a, o) => ({ ...a, [o]: (a[o] ?? 0) + 1 }), {} as Record<string, number>);
  const password = gen?.password ?? row?.proposal?.password ?? null;
  const proposalId = gen?.proposalId ?? row?.proposal?.id ?? null;

  return (
    <>
      <Box sx={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.25)", zIndex: 1000 }} />
      <Box role="dialog" sx={{ position: "fixed", top: ["0", "4vh"], left: "50%", transform: "translateX(-50%)", width: ["100%", "960px"], maxHeight: ["100vh", "92vh"], overflow: "auto", background: "#fff", borderRadius: [0, "10px"], zIndex: 1001, padding: "20px 24px", boxShadow: "0 10px 40px rgba(0,0,0,0.2)" }}>
        <Flex sx={{ justifyContent: "space-between", alignItems: "center", marginBottom: "6px" }}>
          <div style={{ fontFamily: "Poppins", fontSize: 16, fontWeight: 600 }}>Review &amp; send</div>
          <div style={sub}>
            {done ? "Done" : `${index + 1} of ${ids.length}`}
            {" · "}{counts.sent ?? 0} sent · {(counts.skipped ?? 0) + (counts.unavailable ?? 0)} skipped{counts.not_them ? ` · ${counts.not_them} not them` : ""}
          </div>
        </Flex>

        {done && (
          <Box sx={{ padding: "30px 0", textAlign: "center", fontFamily: "Poppins", fontSize: 14 }}>
            {counts.sent ?? 0} site{counts.sent === 1 ? "" : "s"} sent. Every person emailed now has their own lead and a follow-up date.
            <Box sx={{ marginTop: "14px" }}><BasicButton type="button" keepText onClick={onClose}>Close</BasicButton></Box>
          </Box>
        )}

        {!done && loading && <div style={{ ...sub, padding: "40px 0", textAlign: "center" }}>Preparing this site&apos;s proposal…</div>}

        {!done && !loading && row && (alreadySent || unavailable) && (
          <Box sx={{ padding: "20px 0", fontFamily: "Poppins", fontSize: 13 }}>
            <b>{row.site.projectName || row.site.name}</b>:{" "}
            {alreadySent ? `already sent to ${row.sends?.sent} ${row.sends?.sent === 1 ? "person" : "people"}. Follow-ups happen on their leads.` : SKIP_TEXT[gen?.reason ?? ""] ?? gen?.message ?? "Not available"}
            <Box sx={{ marginTop: "12px" }}><BasicButton type="button" keepText onClick={() => next("unavailable")}>Next</BasicButton></Box>
          </Box>
        )}

        {!done && !loading && row && !alreadySent && !unavailable && (
          <Flex sx={{ gap: "24px", flexDirection: ["column", "row"] }}>
            {/* Who */}
            <Box sx={{ flex: ["1 1 auto", "0 0 340px"], minWidth: 0 }}>
              <div style={{ fontFamily: "Poppins", fontSize: 14, fontWeight: 600 }}>{row.site.projectName || row.site.name}</div>
              <div style={sub}>
                {[row.site.city, row.site.state].filter(Boolean).join(", ")}
                {row.installer ? ` · built by ${row.installer.name}` : ""}
                {row.installedAt ? ` · ${moment.utc(row.installedAt).format("MMM YYYY")}` : ""}
                {row.yards != null ? ` · ${row.yards} yd` : ""}
              </div>
              {row.story && (
                <div style={{ display: "inline-block", marginTop: 6, fontFamily: "Poppins", fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 10, background: row.story.depthCheck ? "#FFF8E1" : "#E1F5EE", color: row.story.depthCheck ? "#7A5A00" : "#0F6E56" }}>
                  {BAND_LABEL[row.story.band]}
                </div>
              )}

              <label style={label}>Owner</label>
              <input style={input} value={owner} disabled={!!row.client} onChange={(e) => setOwner(e.target.value)} />
              {row.endCustomer && row.stage !== "confirmed" && (
                <div style={{ ...sub, marginTop: 4, color: "#7A5A00" }}>
                  Suggested by {(row.endCustomer.source ?? "lookup").replace(/_/g, " ")}{row.endCustomer.confidence ? `, ${row.endCustomer.confidence} confidence` : ""}. Sending confirms it.
                </div>
              )}

              <label style={label}>Who gets it · each their own email</label>
              {people.length === 0 && <div style={{ ...sub, color: "#A60000" }}>No email address yet. Add one below.</div>}
              {people.map((c) => {
                const on = picked.includes(c.key);
                const n = chosen.findIndex((x) => x.key === c.key);
                return (
                  <div key={c.key} style={pickRow} onClick={() => toggle(c.key)} role="checkbox" aria-checked={on}>
                    <input type="checkbox" checked={on} readOnly style={check} />
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <Flex sx={{ justifyContent: "space-between", gap: "6px" }}>
                        <span style={{ fontWeight: 600, color: "#222" }}>{c.contact || c.email}</span>
                        {on && <span style={{ fontSize: 10, fontWeight: 600, padding: "1px 6px", borderRadius: 10, background: "#E6F1FB", color: "#185FA5", flex: "none" }}>Email {n + 1}</span>}
                      </Flex>
                      {c.title && <div style={{ color: "#666" }}>{c.title}</div>}
                      <div style={{ color: "#444", wordBreak: "break-all" }}>{c.email}</div>
                      {where(c) && <div style={{ color: "#999", fontSize: 11 }}>via {where(c)}</div>}
                    </div>
                  </div>
                );
              })}
              <Flex sx={{ gap: "6px", marginTop: "6px" }}>
                <input style={input} type="email" placeholder="add an email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} />
                <BasicButton outline scheme="secondary" type="button" keepText onClick={addEmail}>Add</BasicButton>
              </Flex>
            </Box>

            {/* The email */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <label style={{ ...label, marginTop: 0 }}>From</label>
              <select style={input} value={senderId} onChange={(e) => setSenderId(e.target.value)} aria-label="From">
                {!senderId && <option value="">Choose who it is from</option>}
                {senders.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}{s.jobTitle ? `, ${s.jobTitle}` : ""} ({s.email})</option>
                ))}
              </select>
              <div style={{ ...sub, marginTop: 4 }}>Signs the email and the proposal, and gets the replies.</div>

              <Flex sx={{ justifyContent: "space-between", alignItems: "baseline", marginTop: "14px" }}>
                <label style={{ ...label, marginTop: 0 }}>Email</label>
                <span style={sub}>{draftBy === "mission_control" ? "Drafted by Mission Control - edit freely" : draftBy === "user" ? "Your saved draft" : "From this site's story - edit freely"}</span>
              </Flex>
              <input style={input} value={subject} onChange={(e) => setSubject(e.target.value)} aria-label="Subject" />
              <textarea style={{ ...input, marginTop: 8, minHeight: 190, resize: "vertical", lineHeight: 1.5 }} value={message} onChange={(e) => setMessage(e.target.value)} aria-label="Message" />
              <div style={{ ...sub, marginTop: 4 }}>{"{firstName}"} becomes each person&apos;s first name. The email adds a button and their own link{password ? " and password" : ""}.</div>

              {chosen.length > 0 && (
                <Box sx={{ marginTop: "10px", border: "1px solid #EEF0F2", borderRadius: "8px", padding: "10px 12px", background: "#FAFBFC" }}>
                  <Flex sx={{ gap: "6px", flexWrap: "wrap", marginBottom: "8px", alignItems: "center" }}>
                    <span style={{ ...sub, marginRight: 4 }}>As sent to</span>
                    {chosen.map((c) => (
                      <button key={c.key} type="button" style={tab(preview?.key === c.key)} onClick={() => setPreviewKey(c.key)}>{(c.contact || c.email || "").split(" ")[0]}</button>
                    ))}
                  </Flex>
                  <div style={{ fontFamily: "Poppins", fontSize: 12, color: "#222", whiteSpace: "pre-line", lineHeight: 1.55 }}>{fill(message, preview?.contact)}</div>
                </Box>
              )}

              {password && (
                <div style={{ ...pickRow, borderBottom: "none", color: themevals.colors.dkgrey }} onClick={() => setIncludePassword((v) => !v)} role="checkbox" aria-checked={includePassword}>
                  <input type="checkbox" checked={includePassword} readOnly style={check} />
                  <span>Put the password in the email. Off sends it in a second email on its own.</span>
                </div>
              )}
              {proposalId && (
                <div style={{ ...sub, marginTop: 4 }}>
                  <a href={`/proposals/${proposalId}`} target="_blank" rel="noreferrer" style={{ color: "#185FA5" }}>Preview the proposal</a>
                </div>
              )}
            </Box>
          </Flex>
        )}

        {!done && (
          <Flex sx={{ justifyContent: "space-between", gap: "8px", marginTop: "18px", paddingTop: "14px", borderTop: "1px solid #EEF0F2", flexWrap: "wrap" }}>
            <BasicButton outline scheme="secondary" type="button" keepText onClick={onClose}>Stop here</BasicButton>
            {!loading && row && !alreadySent && !unavailable && (
              <Flex sx={{ gap: "8px", flexWrap: "wrap" }}>
                <BasicButton outline scheme="danger" type="button" keepText disabled={!!busy} onClick={notThem}>Not them</BasicButton>
                <BasicButton outline scheme="secondary" type="button" keepText disabled={!!busy} onClick={() => next("skipped")}>Skip</BasicButton>
                <BasicButton type="button" keepText disabled={!!busy || !chosen.length || !subject.trim() || !message.trim() || !senderId} onClick={send}>
                  {busy === "send" ? "Sending…" : chosen.length ? `Send ${chosen.length} email${chosen.length === 1 ? "" : "s"}` : "Pick who gets it"}
                </BasicButton>
              </Flex>
            )}
          </Flex>
        )}
      </Box>
    </>
  );
};

export default ReviewSend;
